import { api, apiBlob, apiUpload } from "./client.js";
import type {
  ChainVerification,
  CreateOrgBody,
  CreateOrgSelfBody,
  OrgInvite,
  CreateOrgInviteBody,
  CreateOrgInviteResult,
  InvitePreview,
  AcceptInviteResult,
  Invoice,
  InvoiceStatus,
  OrgBillingView,
  CloseMonthResult,
  BillingPlan,
  IdentifierGranularity,
  IdentifierScheme,
  Identifier,
  ImportResult,
  Org,
  OrgDetail,
  OrgMemberRole,
  OrgStats,
  OrgWithMemberRole,
  OrgWithCounts,
  OrgStatus,
  Paged,
  Passport,
  PassportDetail,
  PassportVersion,
  PassportVersionSummary,
  Product,
  ProductDetail,
  ProductStatus,
  ProductWithCompliance,
  RegistryRegistration,
  CreateSupplierInviteBody,
  CreateSupplierInviteResult,
  SupplierInvite,
  SupplierInviteStatus,
  SupplierSubmissionReview,
  AccessGrant,
  AccessGrantRole,
  DocumentKind,
  EconomicOperator,
  EffectiveOperator,
  ProductDocument,
  OperatorBody,
  OperatorRole,
  AccessGrantStatus,
  CreateAccessGrantBody,
  CreateAccessGrantResult,
  ApiKey,
  CreateApiKeyBody,
  CreateApiKeyResult,
  Webhook,
  CreateWebhookBody,
  UpdateWebhookBody,
  CreateWebhookResult,
  WebhookDelivery,
  LineageReason,
  DisposalLineBody,
  DisposalOverview,
  DisposalReport,
  EnterpriseSize,
} from "../types.js";

/**
 * Typed wrappers for the DPP API surface (orgs / products / passports).
 * All domain endpoints are org-scoped: the caller passes the selected org id
 * (from OrgContext) explicitly so no request can silently cross tenants.
 */

/* --- Organizations ----------------------------------------------------------- */

export function listMyOrgs(): Promise<OrgWithMemberRole[]> {
  return api<OrgWithMemberRole[]>("/orgs");
}

/** Platform admin (org:manage): every org with member/product/passport counts. */
export function listAllOrgs(includeDemo = false): Promise<OrgWithCounts[]> {
  return api<OrgWithCounts[]>(`/admin/orgs${includeDemo ? "?includeDemo=true" : ""}`);
}

/** Platform admin: lifecycle change (active | suspended | wound_down). */
export function setOrgStatus(orgId: string, status: OrgStatus, reason?: string): Promise<Org> {
  return api<Org>(`/admin/orgs/${orgId}/status`, {
    method: "POST",
    body: reason ? { status, reason } : { status },
  });
}

/** Platform admin: the orgs a user belongs to, with their org role. */
export function listUserOrgs(userId: string): Promise<OrgWithMemberRole[]> {
  return api<OrgWithMemberRole[]>(`/admin/users/${userId}/orgs`);
}

export function getOrgDetail(orgId: string): Promise<OrgDetail> {
  return api<OrgDetail>(`/orgs/${orgId}`);
}

/** Read-only dashboard statistics for the selected org (product:read). */
export function getOrgStats(orgId: string): Promise<OrgStats> {
  return api<OrgStats>(`/orgs/${orgId}/stats`);
}

/** Platform-admin only (org:manage). */
export function createOrg(body: CreateOrgBody): Promise<Org> {
  return api<Org>("/admin/orgs", { method: "POST", body });
}

/** Self-service org creation — any authenticated user; the creator becomes admin. */
export function createOrgSelf(body: CreateOrgSelfBody): Promise<Org> {
  return api<Org>("/orgs", { method: "POST", body });
}

/** Create a sub-organization under `orgId` — caller must be an admin of it. */
export function createSubOrg(orgId: string, body: CreateOrgSelfBody): Promise<Org> {
  return api<Org>(`/orgs/${orgId}/suborgs`, { method: "POST", body });
}

export function addOrgMember(orgId: string, userId: string, role: OrgMemberRole): Promise<void> {
  return api<void>(`/orgs/${orgId}/members`, { method: "POST", body: { userId, role } });
}

export function changeOrgMemberRole(orgId: string, userId: string, role: OrgMemberRole): Promise<void> {
  return api<void>(`/orgs/${orgId}/members/${userId}`, { method: "PATCH", body: { role } });
}

export function removeOrgMember(orgId: string, userId: string): Promise<void> {
  return api<void>(`/orgs/${orgId}/members/${userId}`, { method: "DELETE" });
}

/* --- Org invitations --------------------------------------------------------- */

export function listOrgInvites(orgId: string): Promise<OrgInvite[]> {
  return api<OrgInvite[]>(`/orgs/${orgId}/invites`);
}

export function createOrgInvite(orgId: string, body: CreateOrgInviteBody): Promise<CreateOrgInviteResult> {
  return api<CreateOrgInviteResult>(`/orgs/${orgId}/invites`, { method: "POST", body });
}

export function revokeOrgInvite(orgId: string, inviteId: string): Promise<void> {
  return api<void>(`/orgs/${orgId}/invites/${inviteId}`, { method: "DELETE" });
}

/** Public — no auth (drives the accept page before sign-in). */
export function previewInvite(token: string): Promise<InvitePreview> {
  return api<InvitePreview>(`/invites/preview?token=${encodeURIComponent(token)}`);
}

export function acceptInvite(token: string): Promise<AcceptInviteResult> {
  return api<AcceptInviteResult>("/invites/accept", { method: "POST", body: { token } });
}

/* --- Billing (postpaid) ------------------------------------------------------ */

/** Tenant-facing: this org's current usage + invoice history. */
export function getOrgBilling(orgId: string): Promise<OrgBillingView> {
  return api<OrgBillingView>(`/orgs/${orgId}/billing`);
}

/* Platform-admin billing operations (system:admin). */
export function adminCloseMonth(period?: string): Promise<CloseMonthResult> {
  return api<CloseMonthResult>("/admin/billing/close", {
    method: "POST",
    body: period ? { period } : {},
  });
}

export function adminRunDunning(): Promise<{
  markedOverdue: number;
  viewLimitSet: number;
  viewLimitCleared: number;
}> {
  return api<{ markedOverdue: number; viewLimitSet: number; viewLimitCleared: number }>(
    "/admin/billing/dunning",
    { method: "POST" },
  );
}

export function adminListInvoices(status?: InvoiceStatus): Promise<Invoice[]> {
  return api<Invoice[]>(`/admin/billing/invoices${status ? `?status=${status}` : ""}`);
}

export function adminIssueInvoice(id: string): Promise<Invoice> {
  return api<Invoice>(`/admin/billing/invoices/${id}/issue`, { method: "POST" });
}

export function adminPayInvoice(id: string): Promise<Invoice> {
  return api<Invoice>(`/admin/billing/invoices/${id}/pay`, { method: "POST" });
}

export function adminVoidInvoice(id: string): Promise<Invoice> {
  return api<Invoice>(`/admin/billing/invoices/${id}/void`, { method: "POST" });
}

export function adminSetOrgBilling(
  orgId: string,
  body: { billingPlan: BillingPlan; billingEmail?: string | null },
): Promise<Org> {
  return api<Org>(`/admin/billing/orgs/${orgId}`, { method: "PATCH", body });
}

/* --- Products ----------------------------------------------------------------- */

export interface ListProductsParams {
  limit?: number;
  offset?: number;
  search?: string;
}

export function listProducts(orgId: string, params: ListProductsParams = {}): Promise<Paged<Product>> {
  const q = new URLSearchParams();
  if (params.limit !== undefined) q.set("limit", String(params.limit));
  if (params.offset !== undefined) q.set("offset", String(params.offset));
  if (params.search) q.set("search", params.search);
  const qs = q.toString();
  return api<Paged<Product>>(`/orgs/${orgId}/products${qs ? `?${qs}` : ""}`);
}

export interface CreateProductBody {
  sku: string;
  name: string;
  gtin?: string;
  commodityCode?: string;
  schemaId: string;
  attrs?: Record<string, unknown>;
}

export function createProduct(orgId: string, body: CreateProductBody): Promise<ProductWithCompliance> {
  return api<ProductWithCompliance>(`/orgs/${orgId}/products`, { method: "POST", body });
}

export function getProduct(orgId: string, productId: string): Promise<ProductDetail> {
  return api<ProductDetail>(`/orgs/${orgId}/products/${productId}`);
}

export interface UpdateProductBody {
  sku?: string;
  name?: string;
  gtin?: string | null;
  /** null clears the commodity code. */
  commodityCode?: string | null;
  /** null clears the GTİP. */
  gtip?: string | null;
  schemaId?: string;
  attrs?: Record<string, unknown>;
  status?: ProductStatus;
}

export function updateProduct(
  orgId: string,
  productId: string,
  body: UpdateProductBody,
): Promise<ProductWithCompliance> {
  return api<ProductWithCompliance>(`/orgs/${orgId}/products/${productId}`, { method: "PATCH", body });
}

export function assignIdentifier(
  orgId: string,
  productId: string,
  body: { scheme: IdentifierScheme; granularity: IdentifierGranularity; lot?: string; serial?: string },
): Promise<Identifier> {
  return api<Identifier>(`/orgs/${orgId}/products/${productId}/identifiers`, { method: "POST", body });
}

/** One-way: the identifier keeps resolving, but is flagged and cannot anchor a new passport. */
export function withdrawIdentifier(
  orgId: string,
  productId: string,
  identifierId: string,
  reason?: string,
): Promise<Identifier> {
  return api<Identifier>(`/orgs/${orgId}/products/${productId}/identifiers/${identifierId}/withdraw`, {
    method: "POST",
    body: reason ? { reason } : {},
  });
}

export function importProductsCsv(orgId: string, csv: string): Promise<ImportResult> {
  return api<ImportResult>(`/orgs/${orgId}/products/import`, { method: "POST", body: { csv } });
}

/* --- Passports ----------------------------------------------------------------- */

export interface ListPassportsParams {
  limit?: number;
  offset?: number;
  productId?: string;
}

export function listPassports(orgId: string, params: ListPassportsParams = {}): Promise<Paged<Passport>> {
  const q = new URLSearchParams();
  if (params.limit !== undefined) q.set("limit", String(params.limit));
  if (params.offset !== undefined) q.set("offset", String(params.offset));
  if (params.productId) q.set("productId", params.productId);
  const qs = q.toString();
  return api<Paged<Passport>>(`/orgs/${orgId}/passports${qs ? `?${qs}` : ""}`);
}

export function createPassport(
  orgId: string,
  body: {
    productId: string;
    identifierId?: string;
    originalPassportId?: string;
    originalPassportUri?: string;
    lineageReason?: LineageReason;
  },
): Promise<Passport> {
  return api<Passport>(`/orgs/${orgId}/passports`, { method: "POST", body });
}

/** Lineage (Art. 13(1)(ç)); null clears. Id OR URI, never both. */
export function updatePassportLineage(
  orgId: string,
  passportId: string,
  body: {
    originalPassportId?: string | null;
    originalPassportUri?: string | null;
    lineageReason?: LineageReason | null;
  },
): Promise<Passport> {
  return api<Passport>(`/orgs/${orgId}/passports/${passportId}/lineage`, { method: "PATCH", body });
}

/** Official full-tier dump of one version (Art. 27(15)) — html or JSON-LD, downloaded as a file. */
export function exportPassportVersion(
  orgId: string,
  passportId: string,
  versionNo: number,
  lang: "tr" | "en" | "de",
  format: "html" | "jsonld",
): Promise<Blob> {
  return apiBlob(
    `/orgs/${orgId}/passports/${passportId}/versions/${versionNo}/export?lang=${lang}&format=${format}`,
  );
}

export function getPassport(orgId: string, passportId: string): Promise<PassportDetail> {
  return api<PassportDetail>(`/orgs/${orgId}/passports/${passportId}`);
}

export function publishPassport(
  orgId: string,
  passportId: string,
): Promise<{ passport: Passport; version: PassportVersion }> {
  return api<{ passport: Passport; version: PassportVersion }>(
    `/orgs/${orgId}/passports/${passportId}/publish`,
    { method: "POST" },
  );
}

/** Retention clock inputs; null clears a date. */
export function updatePassportMarketDates(
  orgId: string,
  passportId: string,
  body: { placedOnMarketAt?: string | null; lastUnitPlacedOnMarketAt?: string | null },
): Promise<Passport> {
  return api<Passport>(`/orgs/${orgId}/passports/${passportId}/market-dates`, { method: "PATCH", body });
}

export function archivePassport(orgId: string, passportId: string): Promise<Passport> {
  return api<Passport>(`/orgs/${orgId}/passports/${passportId}/archive`, { method: "POST" });
}

export function listPassportVersions(
  orgId: string,
  passportId: string,
): Promise<{ items: PassportVersionSummary[]; total: number }> {
  return api<{ items: PassportVersionSummary[]; total: number }>(
    `/orgs/${orgId}/passports/${passportId}/versions`,
  );
}

/* --- Product documents --------------------------------------------------------------- */

export function listDocuments(orgId: string, productId: string): Promise<ProductDocument[]> {
  return api<ProductDocument[]>(`/orgs/${orgId}/products/${productId}/documents`);
}

/** Uploads the raw PDF bytes — the server types the file by its magic bytes. */
export function uploadDocument(
  orgId: string,
  productId: string,
  file: File,
  opts: {
    kind: DocumentKind;
    title: string;
    lang: string;
    tier?: "public" | "restricted";
    supersedesId?: string;
    audience?: AccessGrantRole[];
  },
): Promise<ProductDocument> {
  const q = new URLSearchParams({ kind: opts.kind, title: opts.title, lang: opts.lang });
  if (opts.tier) q.set("tier", opts.tier);
  if (opts.audience && opts.audience.length > 0) q.set("audience", opts.audience.join(","));
  if (opts.supersedesId) q.set("supersedesId", opts.supersedesId);
  return apiUpload<ProductDocument>(
    `/orgs/${orgId}/products/${productId}/documents?${q.toString()}`,
    file,
    "application/pdf",
  );
}

export function createDocumentReference(
  orgId: string,
  productId: string,
  body: {
    kind: DocumentKind;
    title: string;
    lang: string;
    tier?: "public" | "restricted";
    audience?: AccessGrantRole[];
    referenceNote: string;
    referenceUrl?: string;
  },
): Promise<ProductDocument> {
  return api<ProductDocument>(`/orgs/${orgId}/products/${productId}/documents/reference`, {
    method: "POST",
    body,
  });
}

export function withdrawDocument(
  orgId: string,
  productId: string,
  documentId: string,
  reason?: string,
): Promise<ProductDocument> {
  return api<ProductDocument>(`/orgs/${orgId}/products/${productId}/documents/${documentId}/withdraw`, {
    method: "POST",
    body: reason ? { reason } : {},
  });
}

/** Authenticated raw bytes for the admin preview (works before any publish). */
export function fetchDocumentRaw(orgId: string, productId: string, documentId: string): Promise<Blob> {
  return apiBlob(`/orgs/${orgId}/products/${productId}/documents/${documentId}/raw`);
}

/* --- Unsold-goods destruction disclosure -------------------------------------------- */

export function getDisposalOverview(orgId: string): Promise<DisposalOverview> {
  return api<DisposalOverview>(`/orgs/${orgId}/disclosures/unsold`);
}
export function saveDisposalReport(
  orgId: string,
  fiscalYear: number,
  body: { note?: string; lines: DisposalLineBody[] },
): Promise<DisposalReport> {
  return api<DisposalReport>(`/orgs/${orgId}/disclosures/unsold/${fiscalYear}`, { method: "PUT", body });
}
export function publishDisposalReport(orgId: string, fiscalYear: number): Promise<DisposalReport> {
  return api<DisposalReport>(`/orgs/${orgId}/disclosures/unsold/${fiscalYear}/publish`, { method: "POST" });
}
export function downloadDisposalExport(orgId: string, fiscalYear: number): Promise<Blob> {
  return apiBlob(`/orgs/${orgId}/disclosures/unsold/${fiscalYear}/export.json`);
}
export function setOrgEnterpriseSize(orgId: string, enterpriseSize: EnterpriseSize | null): Promise<Org> {
  return api<Org>(`/orgs/${orgId}/enterprise-size`, { method: "PATCH", body: { enterpriseSize } });
}

/* --- Economic operators ------------------------------------------------------------ */

export function listOperators(orgId: string): Promise<EconomicOperator[]> {
  return api<EconomicOperator[]>(`/orgs/${orgId}/operators`);
}
export function createOperator(orgId: string, body: OperatorBody): Promise<EconomicOperator> {
  return api<EconomicOperator>(`/orgs/${orgId}/operators`, { method: "POST", body });
}
export function updateOperator(
  orgId: string,
  operatorId: string,
  body: OperatorBody,
): Promise<EconomicOperator> {
  return api<EconomicOperator>(`/orgs/${orgId}/operators/${operatorId}`, { method: "PATCH", body });
}
export function deleteOperator(orgId: string, operatorId: string): Promise<void> {
  return api<void>(`/orgs/${orgId}/operators/${operatorId}`, { method: "DELETE" });
}
export function getProductOperators(orgId: string, productId: string): Promise<EffectiveOperator[]> {
  return api<EffectiveOperator[]>(`/orgs/${orgId}/products/${productId}/operators`);
}
/** Idempotent: null operatorId = fall back to the org default for that role. */
export function putProductOperators(
  orgId: string,
  productId: string,
  assignments: { role: OperatorRole; operatorId: string | null }[],
): Promise<EffectiveOperator[]> {
  return api<EffectiveOperator[]>(`/orgs/${orgId}/products/${productId}/operators`, {
    method: "PUT",
    body: { assignments },
  });
}

/** Customs-broker spreadsheet: every registration's UPI/URI next to the product's CN code and GTİP. */
export function downloadRegistrationsCsv(orgId: string): Promise<Blob> {
  return apiBlob(`/orgs/${orgId}/registry/registrations/export.csv`);
}

/**
 * EU DPP Registry registration. The response is the registration ROW — a
 * failed attempt comes back as status "failed" (with lastError), not an error.
 */
export function registerPassportInRegistry(orgId: string, passportId: string): Promise<RegistryRegistration> {
  return api<RegistryRegistration>(`/orgs/${orgId}/passports/${passportId}/register`, { method: "POST" });
}

export function verifyPassportChain(orgId: string, passportId: string): Promise<ChainVerification> {
  return api<ChainVerification>(`/orgs/${orgId}/passports/${passportId}/verify`, { method: "POST" });
}

/** QR image download (endpoint served by the API; format decided server-side). */
export function downloadPassportQr(orgId: string, passportId: string): Promise<Blob> {
  return apiBlob(`/orgs/${orgId}/passports/${passportId}/qr`);
}

/* --- Supplier data collection ---------------------------------------------------- */

/** The result carries the raw share token EXACTLY once — surface it immediately. */
export function createSupplierInvite(
  orgId: string,
  productId: string,
  body: CreateSupplierInviteBody,
): Promise<CreateSupplierInviteResult> {
  return api<CreateSupplierInviteResult>(`/orgs/${orgId}/products/${productId}/supplier-invites`, {
    method: "POST",
    body,
  });
}

export interface ListSupplierInvitesParams {
  limit?: number;
  offset?: number;
  productId?: string;
  status?: SupplierInviteStatus;
}

export function listSupplierInvites(
  orgId: string,
  params: ListSupplierInvitesParams = {},
): Promise<Paged<SupplierInvite>> {
  const q = new URLSearchParams();
  if (params.limit !== undefined) q.set("limit", String(params.limit));
  if (params.offset !== undefined) q.set("offset", String(params.offset));
  if (params.productId) q.set("productId", params.productId);
  if (params.status) q.set("status", params.status);
  const qs = q.toString();
  return api<Paged<SupplierInvite>>(`/orgs/${orgId}/supplier-invites${qs ? `?${qs}` : ""}`);
}

export function revokeSupplierInvite(orgId: string, inviteId: string): Promise<SupplierInvite> {
  return api<SupplierInvite>(`/orgs/${orgId}/supplier-invites/${inviteId}/revoke`, { method: "POST" });
}

export function getSupplierSubmission(orgId: string, inviteId: string): Promise<SupplierSubmissionReview> {
  return api<SupplierSubmissionReview>(`/orgs/${orgId}/supplier-invites/${inviteId}/submission`);
}

/** Omit fieldIds to apply every submitted field. */
export function applySupplierSubmission(
  orgId: string,
  inviteId: string,
  fieldIds?: string[],
): Promise<{ product: ProductWithCompliance; appliedFieldIds: string[] }> {
  return api<{ product: ProductWithCompliance; appliedFieldIds: string[] }>(
    `/orgs/${orgId}/supplier-invites/${inviteId}/apply`,
    { method: "POST", body: fieldIds ? { fieldIds } : {} },
  );
}

/* --- Restricted-tier access grants (EN 18239) --------------------------------------- */

/** The result carries the raw access token EXACTLY once — surface it immediately. */
export function createAccessGrant(
  orgId: string,
  passportId: string,
  body: CreateAccessGrantBody,
): Promise<CreateAccessGrantResult> {
  return api<CreateAccessGrantResult>(`/orgs/${orgId}/passports/${passportId}/access-grants`, {
    method: "POST",
    body,
  });
}

export interface ListAccessGrantsParams {
  limit?: number;
  offset?: number;
  status?: AccessGrantStatus;
  role?: AccessGrantRole;
}

export function listAccessGrants(
  orgId: string,
  passportId: string,
  params: ListAccessGrantsParams = {},
): Promise<Paged<AccessGrant>> {
  const q = new URLSearchParams();
  if (params.limit !== undefined) q.set("limit", String(params.limit));
  if (params.offset !== undefined) q.set("offset", String(params.offset));
  if (params.status) q.set("status", params.status);
  if (params.role) q.set("role", params.role);
  const qs = q.toString();
  return api<Paged<AccessGrant>>(`/orgs/${orgId}/passports/${passportId}/access-grants${qs ? `?${qs}` : ""}`);
}

export function revokeAccessGrant(orgId: string, passportId: string, grantId: string): Promise<AccessGrant> {
  return api<AccessGrant>(`/orgs/${orgId}/passports/${passportId}/access-grants/${grantId}/revoke`, {
    method: "POST",
  });
}

/* --- Machine integration: API keys & webhooks (integration:manage) ----------------- */

/** The result carries the raw dpk_ key EXACTLY once — surface it immediately. */
export function createApiKey(orgId: string, body: CreateApiKeyBody): Promise<CreateApiKeyResult> {
  return api<CreateApiKeyResult>(`/orgs/${orgId}/api-keys`, { method: "POST", body });
}

export function listApiKeys(orgId: string): Promise<ApiKey[]> {
  return api<ApiKey[]>(`/orgs/${orgId}/api-keys`);
}

export function revokeApiKey(orgId: string, keyId: string): Promise<ApiKey> {
  return api<ApiKey>(`/orgs/${orgId}/api-keys/${keyId}/revoke`, { method: "POST" });
}

/** The result carries the signing secret EXACTLY once — surface it immediately. */
export function createWebhook(orgId: string, body: CreateWebhookBody): Promise<CreateWebhookResult> {
  return api<CreateWebhookResult>(`/orgs/${orgId}/webhooks`, { method: "POST", body });
}

export function listWebhooks(orgId: string): Promise<Webhook[]> {
  return api<Webhook[]>(`/orgs/${orgId}/webhooks`);
}

export function updateWebhook(orgId: string, webhookId: string, body: UpdateWebhookBody): Promise<Webhook> {
  return api<Webhook>(`/orgs/${orgId}/webhooks/${webhookId}`, { method: "PATCH", body });
}

/** Fires a signed "ping" delivery at the endpoint and returns its outcome. */
export function testWebhook(orgId: string, webhookId: string): Promise<WebhookDelivery> {
  return api<WebhookDelivery>(`/orgs/${orgId}/webhooks/${webhookId}/test`, { method: "POST" });
}

export function listWebhookDeliveries(
  orgId: string,
  webhookId: string,
  params: { limit?: number; offset?: number } = {},
): Promise<Paged<WebhookDelivery>> {
  const q = new URLSearchParams();
  if (params.limit !== undefined) q.set("limit", String(params.limit));
  if (params.offset !== undefined) q.set("offset", String(params.offset));
  const qs = q.toString();
  return api<Paged<WebhookDelivery>>(`/orgs/${orgId}/webhooks/${webhookId}/deliveries${qs ? `?${qs}` : ""}`);
}

export function retryWebhookDelivery(
  orgId: string,
  webhookId: string,
  deliveryId: string,
): Promise<WebhookDelivery> {
  return api<WebhookDelivery>(`/orgs/${orgId}/webhooks/${webhookId}/deliveries/${deliveryId}/retry`, {
    method: "POST",
  });
}

/* --- Product media (gallery images) ---------------------------------------- */

export interface ProductMediaItem {
  id: string;
  contentType: string;
  kind: "gallery" | "component";
  byteSize: number;
  sha256: string;
  alt: string | null;
  sortOrder: number;
  createdAt: string;
}

export function listProductMedia(orgId: string, productId: string): Promise<ProductMediaItem[]> {
  return api<ProductMediaItem[]>(`/orgs/${orgId}/products/${productId}/media`);
}

/** Uploads the raw image bytes — the server types the file by its magic bytes. */
export function uploadProductMedia(
  orgId: string,
  productId: string,
  file: File,
  kind: "gallery" | "component" = "gallery",
): Promise<ProductMediaItem> {
  return apiUpload<ProductMediaItem>(
    `/orgs/${orgId}/products/${productId}/media?kind=${kind}`,
    file,
    file.type,
  );
}

export function deleteProductMedia(orgId: string, productId: string, mediaId: string): Promise<void> {
  return api<void>(`/orgs/${orgId}/products/${productId}/media/${mediaId}`, { method: "DELETE" });
}

/** Authenticated raw bytes for the admin thumbnail (works before any publish). */
export function fetchProductMediaRaw(orgId: string, productId: string, mediaId: string): Promise<Blob> {
  return apiBlob(`/orgs/${orgId}/products/${productId}/media/${mediaId}/raw`);
}

/* --- Supply chain master data (facilities, certifications, stages, components) */

export interface Facility {
  id: string;
  name: string;
  countryIso: string;
  city: string | null;
  lat: number | null;
  lng: number | null;
  employeeCount: number | null;
  renewableEnergyPct: number | null;
  waterRecycling: boolean | null;
  wastewaterTreatment: boolean | null;
  practicesNote: string | null;
  identifierScheme: "GLN" | "internal" | null;
  identifierValue: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The certificate's own image (scan or scheme logo) — at most one per certificate. */
export interface CertificationImage {
  id: string;
  contentType: string;
  byteSize: number;
  sha256: string;
  createdAt: string;
}

export interface Certification {
  id: string;
  scheme: string;
  certificateNo: string | null;
  issuer: string | null;
  validFrom: string | null;
  validUntil: string | null;
  evidenceUrl: string | null;
  image: CertificationImage | null;
  createdAt: string;
  updatedAt: string;
}

export interface FacilityBody {
  name: string;
  countryIso: string;
  city?: string;
  lat?: number;
  lng?: number;
  employeeCount?: number;
  renewableEnergyPct?: number;
  waterRecycling?: boolean;
  wastewaterTreatment?: boolean;
  practicesNote?: string;
  /** Unique facility identifier: a GS1 GLN (13 digits) or an org-local code. */
  identifierScheme?: "GLN" | "internal";
  identifierValue?: string;
}

export interface CertificationBody {
  scheme: string;
  certificateNo?: string;
  issuer?: string;
  validFrom?: string;
  validUntil?: string;
  evidenceUrl?: string;
}

export type StageTier = "public" | "restricted";
export type SupplyChainStageKind =
  | "design"
  | "raw_material"
  | "spinning"
  | "weaving"
  | "dyeing"
  | "sampling"
  | "trims"
  | "manufacturing"
  | "delivery";

export interface SupplyChainStage {
  id: string;
  stage: SupplyChainStageKind;
  orderIndex: number;
  facilityId: string | null;
  transportMethod: string | null;
  notes: string | null;
  tier: StageTier;
  audience: AccessGrantRole[] | null;
}

export interface StageBody {
  stage: SupplyChainStageKind;
  facilityId?: string | null;
  transportMethod?: string | null;
  notes?: string | null;
  tier: StageTier;
  /** Restricted tier only: grant roles allowed to see the stage (empty = all). */
  audience?: AccessGrantRole[] | null;
}

export interface ComponentRowDto {
  id: string;
  parentId: string | null;
  name: string;
  componentType: string | null;
  facilityId: string | null;
  imageMediaId: string | null;
  weightG: number | null;
  materialNote: string | null;
  isSeparable: boolean | null;
  tier: StageTier;
  audience: AccessGrantRole[] | null;
  orderIndex: number;
}

export interface ComponentNodeBody {
  name: string;
  componentType?: string | null;
  facilityId?: string | null;
  imageMediaId?: string | null;
  weightG?: number | null;
  materialNote?: string | null;
  isSeparable?: boolean | null;
  tier: StageTier;
  audience?: AccessGrantRole[] | null;
  children?: ComponentNodeBody[];
}

export function listFacilities(orgId: string): Promise<Facility[]> {
  return api<Facility[]>(`/orgs/${orgId}/facilities`);
}
export function getFacility(
  orgId: string,
  id: string,
): Promise<Facility & { certifications: Certification[] }> {
  return api<Facility & { certifications: Certification[] }>(`/orgs/${orgId}/facilities/${id}`);
}
export function createFacility(orgId: string, body: FacilityBody): Promise<Facility> {
  return api<Facility>(`/orgs/${orgId}/facilities`, { method: "POST", body });
}
export function updateFacility(orgId: string, id: string, body: FacilityBody): Promise<Facility> {
  return api<Facility>(`/orgs/${orgId}/facilities/${id}`, { method: "PATCH", body });
}
export function deleteFacility(orgId: string, id: string): Promise<void> {
  return api<void>(`/orgs/${orgId}/facilities/${id}`, { method: "DELETE" });
}
export function setFacilityCertifications(
  orgId: string,
  id: string,
  certificationIds: string[],
): Promise<Certification[]> {
  return api<Certification[]>(`/orgs/${orgId}/facilities/${id}/certifications`, {
    method: "PUT",
    body: { certificationIds },
  });
}

export function listCertifications(orgId: string): Promise<Certification[]> {
  return api<Certification[]>(`/orgs/${orgId}/certifications`);
}
export function listExpiringCertifications(orgId: string, days: number): Promise<Certification[]> {
  return api<Certification[]>(`/orgs/${orgId}/certifications/expiring?days=${days}`);
}
export function createCertification(orgId: string, body: CertificationBody): Promise<Certification> {
  return api<Certification>(`/orgs/${orgId}/certifications`, { method: "POST", body });
}
export function updateCertification(
  orgId: string,
  id: string,
  body: CertificationBody,
): Promise<Certification> {
  return api<Certification>(`/orgs/${orgId}/certifications/${id}`, { method: "PATCH", body });
}
export function deleteCertification(orgId: string, id: string): Promise<void> {
  return api<void>(`/orgs/${orgId}/certifications/${id}`, { method: "DELETE" });
}

/** Uploads the raw image bytes; replaces any existing certificate image. */
export function uploadCertificationImage(orgId: string, id: string, file: File): Promise<CertificationImage> {
  return apiUpload<CertificationImage>(`/orgs/${orgId}/certifications/${id}/image`, file, file.type);
}

export function deleteCertificationImage(orgId: string, id: string): Promise<void> {
  return api<void>(`/orgs/${orgId}/certifications/${id}/image`, { method: "DELETE" });
}

/** Authenticated raw bytes for the admin preview (works before any publish). */
export function fetchCertificationImageRaw(orgId: string, id: string): Promise<Blob> {
  return apiBlob(`/orgs/${orgId}/certifications/${id}/image`);
}

export function getSupplyChain(orgId: string, productId: string): Promise<SupplyChainStage[]> {
  return api<SupplyChainStage[]>(`/orgs/${orgId}/products/${productId}/supply-chain`);
}
export function putSupplyChain(
  orgId: string,
  productId: string,
  stages: StageBody[],
): Promise<SupplyChainStage[]> {
  return api<SupplyChainStage[]>(`/orgs/${orgId}/products/${productId}/supply-chain`, {
    method: "PUT",
    body: { stages },
  });
}

export function getComponents(orgId: string, productId: string): Promise<ComponentRowDto[]> {
  return api<ComponentRowDto[]>(`/orgs/${orgId}/products/${productId}/components`);
}
export function putComponents(
  orgId: string,
  productId: string,
  components: ComponentNodeBody[],
): Promise<ComponentRowDto[]> {
  return api<ComponentRowDto[]>(`/orgs/${orgId}/products/${productId}/components`, {
    method: "PUT",
    body: { components },
  });
}

export function getProductCertifications(orgId: string, productId: string): Promise<Certification[]> {
  return api<Certification[]>(`/orgs/${orgId}/products/${productId}/certifications`);
}
export function putProductCertifications(
  orgId: string,
  productId: string,
  certificationIds: string[],
): Promise<Certification[]> {
  return api<Certification[]>(`/orgs/${orgId}/products/${productId}/certifications`, {
    method: "PUT",
    body: { certificationIds },
  });
}

/* --- LCA, circularity partners, reviews (Faz D) ------------------------------- */

export interface LcaIndicator {
  indicator: string;
  value: number;
  unit: string;
  module?: string;
}

export interface LcaResult {
  id: string;
  source: "manual" | "epd" | "clca";
  externalRef: string | null;
  method: string | null;
  functionalUnit: string | null;
  systemBoundary: string | null;
  dataset: string | null;
  calculatedAt: string | null;
  verifiedBy: string | null;
  indicators: LcaIndicator[];
  updatedAt: string;
}

export interface LcaPutBody {
  source: "manual" | "epd" | "clca";
  externalRef?: string | null;
  method?: string | null;
  functionalUnit?: string | null;
  systemBoundary?: string | null;
  dataset?: string | null;
  calculatedAt?: string | null;
  verifiedBy?: string | null;
  indicators: LcaIndicator[];
}

export function getLca(orgId: string, productId: string): Promise<LcaResult | null> {
  return api<LcaResult | null>(`/orgs/${orgId}/products/${productId}/lca`);
}
export function putLca(orgId: string, productId: string, body: LcaPutBody): Promise<LcaResult> {
  return api<LcaResult>(`/orgs/${orgId}/products/${productId}/lca`, { method: "PUT", body });
}
export function deleteLca(orgId: string, productId: string): Promise<void> {
  return api<void>(`/orgs/${orgId}/products/${productId}/lca`, { method: "DELETE" });
}

/** The five categories that ship with localized labels. */
export type PartnerCategory = "donation" | "repair" | "upcycle" | "recycle" | "resale";

export interface Partner {
  id: string;
  name: string;
  /** A PartnerCategory key, or an org-defined label typed by the user. */
  category: string;
  countryIso: string | null;
  city: string | null;
  url: string | null;
}

export interface PartnerBody {
  name: string;
  category: string;
  countryIso?: string;
  city?: string;
  url?: string;
}

export function listPartners(orgId: string): Promise<Partner[]> {
  return api<Partner[]>(`/orgs/${orgId}/partners`);
}
export function createPartner(orgId: string, body: PartnerBody): Promise<Partner> {
  return api<Partner>(`/orgs/${orgId}/partners`, { method: "POST", body });
}
export function updatePartner(orgId: string, id: string, body: PartnerBody): Promise<Partner> {
  return api<Partner>(`/orgs/${orgId}/partners/${id}`, { method: "PATCH", body });
}
export function deletePartner(orgId: string, id: string): Promise<void> {
  return api<void>(`/orgs/${orgId}/partners/${id}`, { method: "DELETE" });
}
export function getProductPartners(orgId: string, productId: string): Promise<Partner[]> {
  return api<Partner[]>(`/orgs/${orgId}/products/${productId}/partners`);
}
export function putProductPartners(
  orgId: string,
  productId: string,
  partnerIds: string[],
): Promise<Partner[]> {
  return api<Partner[]>(`/orgs/${orgId}/products/${productId}/partners`, {
    method: "PUT",
    body: { partnerIds },
  });
}

export interface ReviewItem {
  id: string;
  productId: string;
  productName: string;
  authorName: string | null;
  rating: number;
  body: string | null;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
}

export function listReviews(
  orgId: string,
  status?: "pending" | "approved" | "rejected",
): Promise<ReviewItem[]> {
  return api<ReviewItem[]>(`/orgs/${orgId}/reviews${status ? `?status=${status}` : ""}`);
}
export function moderateReview(
  orgId: string,
  reviewId: string,
  status: "approved" | "rejected",
): Promise<ReviewItem> {
  return api<ReviewItem>(`/orgs/${orgId}/reviews/${reviewId}/moderate`, {
    method: "POST",
    body: { status },
  });
}

/* --- Theme & scan analytics (Faz E) ------------------------------------------- */

export interface OrgThemeDto {
  accentColor?: string;
  disabledSections?: string[];
  sectionLabels?: Record<string, string>;
}

export function getOrgTheme(orgId: string): Promise<OrgThemeDto> {
  return api<OrgThemeDto>(`/orgs/${orgId}/theme`);
}
export function putOrgTheme(orgId: string, theme: OrgThemeDto): Promise<OrgThemeDto> {
  return api<OrgThemeDto>(`/orgs/${orgId}/theme`, { method: "PUT", body: theme });
}

export interface ScanDayCount {
  day: string;
  count: number;
}

export function getScanStats(orgId: string, days = 30): Promise<ScanDayCount[]> {
  return api<ScanDayCount[]>(`/orgs/${orgId}/scan-stats?days=${days}`);
}
